import { GOOGLE_MAPS_API_KEY } from './env';

/**
 * Loads the Google Maps JavaScript API, once per page.
 *
 * The same hand-rolled loader as the admin panel, minus the libraries this app
 * has no use for: the whole job is a script tag and a promise, and what matters
 * is the caching — the API throws if its script is added twice, and a rider
 * moving between two deliveries mounts the map twice in a row.
 *
 * Only loaded when a map is actually on screen, so the run sheet never pays
 * for it on a phone connection.
 *
 * The key is the same browser key as the old still preview, and needs the
 * "Maps JavaScript API" enabled on it in the Cloud console.
 */

const CALLBACK = '__tlpRiderMapsReady';

type MapsApi = typeof google.maps;

let pending: Promise<MapsApi> | null = null;

/**
 * Google reports a rejected key (or the API not enabled on it) by calling this
 * global and drawing its own grey error over the map — the script itself still
 * loads, so the promise resolves. Recording it is the only way to tell.
 */
let authFailed = false;

export function googleMapsAuthFailed(): boolean {
  return authFailed;
}

export class MissingApiKeyError extends Error {
  constructor() {
    super('VITE_GOOGLE_MAPS_API_KEY is not set');
    this.name = 'MissingApiKeyError';
  }
}

export function loadGoogleMaps(): Promise<MapsApi> {
  if (pending) return pending;

  if (!GOOGLE_MAPS_API_KEY) {
    // Not cached: setting the key and reloading should just work.
    return Promise.reject(new MissingApiKeyError());
  }

  pending = new Promise<MapsApi>((resolve, reject) => {
    if (window.google?.maps) {
      resolve(window.google.maps);
      return;
    }

    (window as unknown as Record<string, unknown>)[CALLBACK] = () => resolve(window.google.maps);
    (window as unknown as Record<string, unknown>).gm_authFailure = () => {
      authFailed = true;
    };

    const script = document.createElement('script');
    const params = new URLSearchParams({
      key: GOOGLE_MAPS_API_KEY,
      loading: 'async',
      callback: CALLBACK,
      v: 'weekly',
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
    script.async = true;
    script.onerror = () => {
      pending = null;
      reject(new Error('Could not reach Google Maps'));
    };
    document.head.appendChild(script);
  });

  return pending;
}
